import React, { useState, useRef, useEffect } from "react";
import svgPaths from "./imports/svg-gm4fmr4ck4";
import {
  getNextMonthDate,
  formatDateToHumanReadable,
} from "../../../utils/utils";

// --- Utility Components ---

function Close({ onClose }) {
  return (
    <div
      className="relative shrink-0 size-6"
      data-name="Close"
      onClick={onClose}
    >
      <svg
        className="block size-full"
        fill="none"
        preserveAspectRatio="none"
        viewBox="0 0 24 24"
      >
        <g id="Close">
          <path
            d="M18 6L6 18M6 6L18 18"
            id="Icon"
            stroke="var(--stroke-0, #211F26)"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="1.5"
          />
        </g>
      </svg>
    </div>
  );
}

function HDivider() {
  return <div className="h-px bg-[#eae7ec] w-full" data-name="H Divider" />;
}

function Frame32({ onClose }) {
  return (
    <div className="box-border flex items-center justify-between gap-4 sm:gap-6 p-0 w-full relative">
      <div className="font-['Geist:Medium',_sans-serif] text-[#211f26] text-lg sm:text-2xl tracking-[-0.24px] flex-1">
        <p>Update Subscription</p>
      </div>
      <Close onClose={onClose} />
    </div>
  );
}

function PriceDisplay({ price, billingPeriod }) {
  return (
    <div className="flex flex-col gap-1 items-start">
      <div className="flex items-baseline text-[#2d58ff] font-['Geist:SemiBold',_sans-serif]">
        <span className="text-2xl sm:text-3xl leading-tight">{price}</span>
      </div>
      <div className="font-['Geist:Regular',_sans-serif] text-[#211f26] text-sm">
        {billingPeriod}
      </div>
    </div>
  );
}

function PlanCard({ title, price, billingPeriod }) {
  return (
    <div className="bg-[#f2eff3] border border-[#eae7ec] rounded-2xl w-full">
      <div className="flex items-center justify-between p-4">
        <div className="font-['Geist:SemiBold',_sans-serif] text-[#211f26] text-xl sm:text-2xl tracking-[-0.24px]">
          {title}
        </div>
        <PriceDisplay price={price} billingPeriod={billingPeriod} />
      </div>
    </div>
  );
}

function UpgradeIndicator() {
  return (
    <div className="flex justify-center w-full">
      <div className="bg-[#f2eff3] border border-[#eae7ec] rounded-lg size-8 flex items-center justify-center">
        <svg className="size-6" fill="none" viewBox="0 0 24 24">
          <path
            d="M6 9L12 15L18 9"
            stroke="#211F26"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="1.5"
          />
        </svg>
      </div>
    </div>
  );
}

function ExpandButton({ isExpanded, onToggle }) {
  return (
    <button
      onClick={onToggle}
      className="flex items-center justify-between w-full cursor-pointer hover:opacity-80 transition-opacity"
    >
      <div className="font-['Geist:Medium',_sans-serif] text-[#211f26] text-sm sm:text-base">
        See how your total was calculated
      </div>
      <div
        className={`size-6 transition-transform duration-200 ${
          isExpanded ? "rotate-180" : ""
        }`}
      >
        <svg className="size-full" fill="none" viewBox="0 0 24 24">
          <path
            d="M6 9L12 15L18 9"
            stroke="#211F26"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="1.5"
          />
        </svg>
      </div>
    </button>
  );
}

function InfoTooltip({ isVisible, tooltipRef }) {
  return (
    <div
      ref={tooltipRef}
      className={`absolute bg-[#fdfcfd] border border-[#eae7ec] rounded-2xl p-4 w-80 max-w-[90vw] shadow-lg transition-all duration-200 ${
        isVisible
          ? "opacity-100 scale-100 z-[60]"
          : "opacity-0 scale-95 pointer-events-none z-[-1]"
      }`}
      style={{ top: "calc(100% + 8px)", right: "0" }}
      data-name="Tooltip"
    >
      <div className="font-['Geist:Regular',_sans-serif] text-[#211f26] text-sm sm:text-base">
        If you're changing plans before your current billing cycle ends, we
        calculate how much credit is left and apply that value as a credit
        towards your new plan.
      </div>
    </div>
  );
}

function InfoIcon({ onToggleTooltip, isTooltipVisible, tooltipRef }) {
  return (
    <div className="relative flex items-center justify-center">
      <button
        onClick={onToggleTooltip}
        className="size-4 cursor-pointer hover:opacity-80 transition-opacity"
        data-name="info-circle"
      >
        <svg
          className="block size-full"
          fill="none"
          preserveAspectRatio="none"
          viewBox="0 0 16 16"
        >
          <g clipPath="url(#clip0_1_322)" id="info-circle">
            <path
              d={svgPaths.p3b51300}
              id="Icon"
              stroke="var(--stroke-0, #211F26)"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="1.4"
            />
          </g>
          <defs>
            <clipPath id="clip0_1_322">
              <rect fill="white" height="16" width="16" />
            </clipPath>
          </defs>
        </svg>
      </button>
      <InfoTooltip isVisible={isTooltipVisible} tooltipRef={tooltipRef} />
    </div>
  );
}

function CalculationRow({
  label,
  amount,
  hasInfo = false,
  onToggleTooltip,
  isTooltipVisible,
  tooltipRef,
}) {
  return (
    <div className="flex justify-between w-full">
      <div className="flex items-center gap-1">
        <div className="font-['Geist:Regular',_sans-serif] text-[#211f26] text-sm sm:text-base">
          {label}
        </div>
        {hasInfo && onToggleTooltip && (
          <InfoIcon
            onToggleTooltip={onToggleTooltip}
            isTooltipVisible={isTooltipVisible || false}
            tooltipRef={tooltipRef}
          />
        )}
      </div>
      <div className="font-['Geist:Medium',_sans-serif] text-[#211f26] text-sm">
        {amount}
      </div>
    </div>
  );
}

// The next charge lands at the end of the current Stripe billing period, which
// the API returns as plan.subscriptionEndDate. Deriving it from the
// subscription's creation date drifts by a period on every renewal, so only
// fall back to that when the period end is missing.
const getNextBillingDateLabel = (subscription?: any, activePlan?: any) => {
  const periodEnd =
    subscription?.plan?.subscriptionEndDate ||
    subscription?.activePlan?.subscriptionEndDate ||
    subscription?.subscriptionEndDate;

  if (periodEnd) {
    return formatDateToHumanReadable(periodEnd);
  }

  if (activePlan?.createdAt) {
    return formatDateToHumanReadable(getNextMonthDate(activePlan.createdAt));
  }

  return "-";
};

const getDaysUntilNextBilling = (subscription?: any) => {
  const periodEnd =
    subscription?.plan?.subscriptionEndDate ||
    subscription?.activePlan?.subscriptionEndDate ||
    subscription?.subscriptionEndDate;

  if (!periodEnd) {
    return null;
  }

  const days = Math.ceil(
    (new Date(periodEnd).getTime() - Date.now()) / (1000 * 60 * 60 * 24),
  );

  return Number.isFinite(days) && days > 0 ? days : null;
};

const normalizeBillingCurrency = (currency?: string | null) => {
  return currency?.toUpperCase() === "INR" ? "INR" : "USD";
};

const getRegisteredBillingCurrency = (
  subscription?: any,
  customPlanDetails?: any,
) => {
  return normalizeBillingCurrency(
    customPlanDetails?.currency ||
      subscription?.currency ||
      subscription?.plan?.currency ||
      subscription?.plan?.metadata?.currency,
  );
};

const formatBillingAmount = (amount: number | string, currency?: string) => {
  const parsedAmount = Number(amount);

  if (Number.isNaN(parsedAmount)) {
    return "-";
  }

  const amountText = Number.isInteger(parsedAmount)
    ? String(parsedAmount)
    : parsedAmount.toFixed(2).replace(/\.?0+$/, "");
  const symbol = normalizeBillingCurrency(currency) === "INR" ? "₹" : "$";

  return `${symbol}${amountText}`;
};

function ExpandedCalculation({
  isExpanded,
  onToggleTooltip,
  isTooltipVisible,
  tooltipRef,
  activePlan,
  selectedPlan,
  subscription,
  customPlanDetails,
}) {
  const priceDiff = (
    selectedPlan.planPriceInFig - activePlan.planPriceInFig
  ).toFixed(2);
  const currency =
    selectedPlan.currency ||
    activePlan.currency ||
    getRegisteredBillingCurrency(subscription, customPlanDetails);
  const daysUntilNextBilling = getDaysUntilNextBilling(subscription);

  return (
    <div
      className={`bg-[#f2eff3] border border-[#eae7ec] rounded-2xl w-full transition-all duration-300 ${
        isExpanded
          ? "opacity-100 max-h-96"
          : "opacity-0 max-h-0 overflow-hidden"
      }`}
    >
      <div className="p-4 flex flex-col gap-4">
        <CalculationRow
          label={`New plan (${selectedPlan.name})`}
          amount={selectedPlan.price}
        />
        <CalculationRow
          label="Credit from current plan"
          amount={
            priceDiff < 0
              ? "-"
              : `-${formatBillingAmount(activePlan.planPriceInFig, currency)}`
          }
          hasInfo
          onToggleTooltip={onToggleTooltip}
          isTooltipVisible={isTooltipVisible}
          tooltipRef={tooltipRef}
        />
        <HDivider />
        <div className="flex justify-between">
          <div className="font-['Geist:Medium',_sans-serif] text-[#2d58ff] text-sm sm:text-base">
            {selectedPlan.name === "Free" || priceDiff > 0 ? (
              <>Amount Due Today</>
            ) : (
              <>
                Amount Due on{" "}
                {getNextBillingDateLabel(subscription, activePlan)}
              </>
            )}
          </div>
          <div className="font-['Geist:Medium',_sans-serif] text-[#2d58ff] text-sm">
            {selectedPlan.name === "Free"
              ? "Free"
              : priceDiff < 0
              ? selectedPlan.price
              : formatBillingAmount(priceDiff, currency)}
          </div>
        </div>
        <div className="font-['Geist:Regular',_sans-serif] text-[#211f26] text-xs sm:text-sm">
          {priceDiff < 0 ? (
            <>
              Your downgrade is scheduled. No changes or charges will happen
              now. You’ll stay on the current plan until{" "}
              {getNextBillingDateLabel(subscription, activePlan)}
              , and your next plan will begin immediately after that.
            </>
          ) : (
            <>
              You’ll be billed today for a prorated upgrade fee of{" "}
              {formatBillingAmount(priceDiff, currency)} and your account will
              be upgraded immediately. Your next payment
              {daysUntilNextBilling ? ` in ${daysUntilNextBilling} days` : ""}{" "}
              will be{" "}
              {formatBillingAmount(selectedPlan.planPriceInFig, currency)} on{" "}
              {getNextBillingDateLabel(subscription, activePlan)}
              .
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function ProceedButton({ onProceed }) {
  return (
    <button
      onClick={onProceed}
      className="bg-[#2d58ff] hover:bg-[#2448e6] h-12 rounded-2xl w-full cursor-pointer transition-colors flex items-center justify-center"
      data-name="Button base"
    >
      <div className="font-['Geist:Medium',_sans-serif] text-[#fcfcfd] text-sm">
        Proceed
      </div>
    </button>
  );
}

const priceToShow = (subscription, customPlanDetails) => {
  const currency = getRegisteredBillingCurrency(subscription, customPlanDetails);

  // Case 1: Custom plan and we have pre-downgrade data → show custom plan upgrade price
  if (subscription.plan?.name?.toLowerCase() === "custom") {
    if (subscription && subscription.preDowngradeStripePriceData) {
      return formatBillingAmount(
        customPlanDetails?.downgradeData?.price,
        currency,
      );
    }

    // // Case 2: If downgrade data exists → show that price
    // if (customPlanDetails?.upgradeData?.price) {
    //   return `${currency} ${customPlanDetails.upgradeData.price}`;
    // }

    const baseAmount = subscription?.preDowngradeStripePriceData
      ? subscription.preDowngradeStripePriceData.amount
      : subscription?.plan?.amount ?? 0;

    return formatBillingAmount(baseAmount / 100, currency);
  }

  if (
    subscription?.preDowngradeStripePriceData?.name?.toLowerCase() === "custom"
  ) {
    if (customPlanDetails?.downgradeData?.price) {
      return formatBillingAmount(customPlanDetails.downgradeData.price, currency);
    }

    const baseAmount = subscription?.preDowngradeStripePriceData
      ? subscription.preDowngradeStripePriceData.amount
      : subscription?.plan?.amount ?? 0;

    return formatBillingAmount(baseAmount / 100, currency);
  }

  // Case 3: Otherwise fallback to pre-downgrade amount or subscription plan amount
  const baseAmount = subscription?.preDowngradeStripePriceData
    ? subscription.preDowngradeStripePriceData.amount
    : subscription?.plan?.amount ?? 0;

  return formatBillingAmount(baseAmount / 100, currency);
};

// --- Main ModalContent ---

function ModalContent({
  isExpanded,
  onToggle,
  onToggleTooltip,
  onProceed,
  isTooltipVisible,
  tooltipRef,
  selectedPlan,
  onClose,
  activePlan,
  customPlanDetails,
  subscription,
}) {
  const priceDiff = (
    selectedPlan.planPriceInFig - activePlan.planPriceInFig
  ).toFixed(2);
  const currency =
    selectedPlan.currency ||
    activePlan.currency ||
    getRegisteredBillingCurrency(subscription, customPlanDetails);

  const activePlanPrice = priceToShow(subscription, customPlanDetails);

  return (
    <div className="flex flex-col gap-6 sm:gap-8 w-full max-w-[400px] relative">
      <Frame32 onClose={onClose} />
      <HDivider />
      <div className="flex flex-col gap-6 w-full">
        <div className="flex flex-col gap-4 relative">
          <div className="flex flex-col gap-1">
            <div className="font-['Geist:Regular',_sans-serif] text-[#211f26] text-sm">
              Current billing plan
            </div>
            <PlanCard
              title={activePlan.name}
              price={activePlanPrice}
              billingPeriod="Billed monthly"
            />
          </div>
          <UpgradeIndicator />
          <div className="flex flex-col gap-1">
            <div className="font-['Geist:Regular',_sans-serif] text-[#211f26] text-sm">
              Next billing plan:{" "}
              {getNextBillingDateLabel(subscription, activePlan)}
            </div>
            <PlanCard
              title={selectedPlan.name}
              price={selectedPlan.price}
              billingPeriod="Billed monthly"
            />
          </div>
          <ExpandButton isExpanded={isExpanded} onToggle={onToggle} />
          <ExpandedCalculation
            isExpanded={isExpanded}
            onToggleTooltip={onToggleTooltip}
            isTooltipVisible={isTooltipVisible}
            tooltipRef={tooltipRef}
            activePlan={activePlan}
            selectedPlan={selectedPlan}
            subscription={subscription}
            customPlanDetails={customPlanDetails}
          />
        </div>
        <HDivider />
        <div className="flex justify-between">
          <div className="font-['Geist:Medium',_sans-serif] text-[#211f26] text-sm sm:text-base">
            Amount Due Today
          </div>
          <div className="font-['Geist:Medium',_sans-serif] text-[#211f26] text-sm sm:text-base">
            {formatBillingAmount(priceDiff < 0 ? 0 : priceDiff, currency)}
          </div>
        </div>
        <ProceedButton onProceed={onProceed} />
      </div>
    </div>
  );
}

// --- Final Container ---

function SideModal(props) {
  const { isTooltipVisible } = props;
  return (
    <div
      className={`h-full w-full transition-all duration-300 z-40 ${
        isTooltipVisible ? "overflow-visible" : "overflow-y-auto"
      }`}
    >
      <div className="p-4 sm:p-14 h-full flex flex-col justify-start">
        <ModalContent {...props} />
      </div>
    </div>
  );
}

// --- Custom Hook for Click Outside Tooltip ---

function useClickOutside(ref, isActive, onClose) {
  useEffect(() => {
    if (!isActive) return;
    const handleClick = (e) => {
      const target = e.target;
      if (
        ref.current &&
        !ref.current.contains(target) &&
        !target.closest('[data-name="info-circle"]')
      ) {
        onClose();
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [ref, isActive, onClose]);
}

// --- Main Export ---

const ChangeSubscriptionModal = ({
  activePlan,
  selectedPlan,
  handleChangePlan,
  onClose,
  customPlanDetails,
  subscription,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isTooltipVisible, setIsTooltipVisible] = useState(false);
  const tooltipRef = useRef(null);

  const handleToggleExpanded = () => setIsExpanded((prev) => !prev);
  const handleToggleTooltip = () => setIsTooltipVisible((prev) => !prev);
  const handleProceed = () => {
    const c = confirm("Proceeding with subscription update...");
    if (c) handleChangePlan();
  };

  useClickOutside(tooltipRef, isTooltipVisible, () =>
    setIsTooltipVisible(false)
  );

  return (
    <div
      className="bg-[#ffffff] size-full"
      data-name="Billing - Upgrade page - low to higher plan"
    >
      <SideModal
        isExpanded={isExpanded}
        onToggle={handleToggleExpanded}
        onToggleTooltip={handleToggleTooltip}
        onProceed={handleProceed}
        isTooltipVisible={isTooltipVisible}
        tooltipRef={tooltipRef}
        activePlan={activePlan}
        selectedPlan={selectedPlan}
        onClose={onClose}
        customPlanDetails={customPlanDetails}
        subscription={subscription}
      />
    </div>
  );
};

export default ChangeSubscriptionModal;
